import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useGroups } from '@/hooks/useGroups';
import { usePeriodSettings, formatBrowserDate } from '@/hooks/usePeriodSettings';
import {
  fetchStatisticsData,
  computeStatistics,
  getDateRange,
  TIME_RANGE_KEYS,
} from '@/api/statistics';
import type { TimeRange, StatisticsResult } from '@/api/statistics';
import { resolveStatsPeriod, formatStatsPeriodLabels } from '@sharecost/shared';
import type { StatsPeriodLabels } from '@sharecost/shared';

/**
 * Labels for the selected statistics period ("September 2026", "2026", ...)
 * following the user's week/month start; "All time" for the unbounded range.
 */
export function useStatsPeriodLabels(timeRange: TimeRange, periodOffset: number): StatsPeriodLabels {
  const settings = usePeriodSettings();
  const { t } = useTranslation(['groups']);
  return useMemo(() => {
    if (timeRange === 'ALL') return { title: t('stats.range.allTime'), subtitle: null };
    const spec = resolveStatsPeriod(TIME_RANGE_KEYS[timeRange], settings, periodOffset);
    return formatStatsPeriodLabels(spec, settings, formatBrowserDate);
  }, [timeRange, periodOffset, settings, t]);
}

export function useStatistics(timeRange: TimeRange, selectedGroupId?: string, periodOffset: number = 0) {
  const { user, profile } = useAuth();
  const { data: groups } = useGroups();
  const settings = usePeriodSettings();

  return useQuery<StatisticsResult>({
    // Settings and offset change the resolved period, so they are part of the key
    queryKey: ['statistics', user?.id, timeRange, selectedGroupId, periodOffset, settings],
    queryFn: async () => {
      const filteredGroups = selectedGroupId
        ? groups!.filter(g => g.id === selectedGroupId)
        : groups!;

      const groupIds = filteredGroups.map(g => g.id);
      const dateRange = getDateRange(timeRange, settings, periodOffset);
      const data = await fetchStatisticsData(groupIds, dateRange);

      // Display currency: single group → group currency, all → user's default
      const displayCurrency = selectedGroupId
        ? filteredGroups[0]?.currency_code ?? profile?.default_currency ?? 'USD'
        : profile?.default_currency ?? 'USD';

      return computeStatistics(data, user!.id, displayCurrency, filteredGroups, timeRange, settings);
    },
    enabled: !!user && !!groups && groups.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}
